import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Switch,
  Typography,
  Chip,
  Button,
  FormControlLabel,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { BossBabyTaskDto, BossBabyCompanySettingDto } from '@/dto/BossBabyDto';

interface CompanyTaskToggleProps {
  task: BossBabyTaskDto & { companySetting?: BossBabyCompanySettingDto };
  onToggle: (taskId: number, isEnabled: boolean) => void;
  onCustomConfig?: (taskId: number) => void;
}

export const CompanyTaskToggle: React.FC<CompanyTaskToggleProps> = ({
  task,
  onToggle,
  onCustomConfig,
}) => {
  const getTaskStatus = () => {
    if (task.companySetting) {
      return task.companySetting.isEnabled ? 'Company Enabled' : 'Company Disabled';
    }
    return task.isActive ? 'Global Active' : 'Global Inactive';
  };

  const getStatusColor = () => {
    if (task.companySetting) {
      return task.companySetting.isEnabled ? 'success' : 'error';
    }
    return task.isActive ? 'info' : 'default';
  };

  const isEnabled = task.companySetting?.isEnabled ?? task.isActive;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" component="div" sx={{ mb: 1 }}>
              {task.taskName}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {task.description}
            </Typography>

            {/* Tools Used */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Tools Used:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {task.tools.slice(0, 2).map((tool, index) => (
                  <Chip 
                    key={index}
                    label={tool} 
                    size="small" 
                    variant="outlined"
                    color="secondary"
                    sx={{ fontSize: '0.75rem', height: '20px' }}
                  />
                ))}
                {task.tools.length > 2 && (
                  <Chip 
                    label={`+${task.tools.length - 2} more`}
                    size="small" 
                    variant="outlined"
                    color="default"
                    sx={{ fontSize: '0.75rem', height: '20px' }}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip 
                label={getTaskStatus()} 
                size="small" 
                color={getStatusColor() as any}
              />
              <Chip 
                label={task.priorityLevel} 
                size="small" 
                color={task.priorityLevel === 'HIGH' ? 'error' : task.priorityLevel === 'MEDIUM' ? 'warning' : 'info'}
              />
              <Chip 
                label={task.executionFrequency} 
                size="small" 
                variant="outlined"
              />
            </Box>

            {/* Example Message */}
            {task.exampleMessage && (
              <Box sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Example:
                </Typography>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontStyle: 'italic',
                    fontSize: '0.875rem',
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  "{task.exampleMessage}"
                </Typography>
              </Box>
            )}

            <Typography variant="caption" color="text.secondary" display="block">
              {task.companySetting ? 'Company Setting: Custom' : 'Company Setting: Using Global'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={isEnabled}
                  onChange={(e) => onToggle(task.taskId, e.target.checked)}
                  color="primary"
                />
              }
              label={isEnabled ? 'Enabled' : 'Disabled'}
              labelPlacement="start"
              sx={{ m: 0 }}
            />

            {onCustomConfig && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<SettingsIcon />}
                onClick={() => onCustomConfig(task.taskId)}
              >
                Custom Config
              </Button>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};